import { useEffect } from 'react';
import { useCustomerDispatch } from '@olo-web/client-state';
import { loadSession } from '@olo-web/domain/customer/functions/sessionPersistence';

// How long we'll wait on secure storage before letting the UI proceed anyway.
// This guarantees gated UI (e.g. the sign-in button) is never hidden forever if
// a native storage call hangs on the Capacitor bridge.
const HYDRATION_TIMEOUT_MS = 1500;

// Restores a persisted customer session from secure storage on app launch
// (native-only). On web `loadSession` resolves to null immediately, so this
// simply flips the hydration flag with no behavioral change.
export function useRehydrateSession(): void {
  const customerDispatch = useCustomerDispatch();

  useEffect(() => {
    let isMounted = true;
    let hasFlippedHydrated = false;

    const markHydrated = () => {
      if (!isMounted || hasFlippedHydrated) return;
      hasFlippedHydrated = true;
      customerDispatch({ type: 'SET_AUTH_HYDRATED', payload: true });
    };

    // Safety net: proceed even if secure storage never resolves.
    const timeoutId = setTimeout(markHydrated, HYDRATION_TIMEOUT_MS);

    loadSession()
      .then((session) => {
        if (isMounted && session) {
          customerDispatch({
            type: 'UPDATE',
            payload: {
              customerId: session.customerId,
              sessionId: session.sessionId,
              sessionSecret: session.sessionSecret,
              loginTimestamp: session.loginTimestamp,
              firstName: session.firstName,
              lastName: session.lastName,
            },
          });
        }
      })
      .catch(() => {
        // loadSession already swallows errors; this is belt-and-suspenders.
      })
      .finally(markHydrated);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [customerDispatch]);
}
